import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MenuIcon from '@mui/icons-material/Menu';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import SearchIcon from '@mui/icons-material/Search';
import AppBar from '@mui/material/AppBar';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Dialog from '@mui/material/Dialog';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import OutlinedInput from '@mui/material/OutlinedInput';
import Popover from '@mui/material/Popover';
import Slide from '@mui/material/Slide';
import Stack from '@mui/material/Stack';
import Toolbar from '@mui/material/Toolbar';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import type { TransitionProps } from '@mui/material/transitions';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Form, useFetcher, useLocation, useMatches, useRouteLoaderData, useSearchParams } from '@remix-run/react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import Image from 'remix-image';
import { StarSetAnimatedIcon } from '~/components/Automockups/AutomockupButton';
import { AutoMockupContainer } from '~/components/Automockups/AutomockupContainer';
import { cartWidth } from '~/components/Cart';
import Logo from '~/components/Logo';
import AnnounceKitComponent from '~/components/Navbar/AnnounceKitComponent';
import { CartButton, MenuEntry, ModalMenuId, menuItemList } from '~/components/Navbar/common';
import useHeader from '~/components/hooks/useHeader';
import SeeMoreDescription from '~/components/shared/SeeMoreDescription';
import { MuiRemixLink } from '~/components/shared/links';
import type { LoaderData as RootLoaderData } from '~/root';
import { dashboardLinks } from '~/routes/dashboard';
import type { Artwork, Cart, UserResponse } from '~/services/api.server';
import { ellipsisStyle } from '~/utils/common-styles';
import { useAuth, useFlags } from '~/utils/contexts';
import { AUTOMOCKUPS_PARAM, DOMAIN_PARAM, LOGO_PARAM, SEARCH_PARAM } from '~/utils/searchParams';
import { Status, statusColors } from './shared/Status';

type UserAuthFormProps = {
  handleModalClose?: () => void;
};
export function UserAuthForm({ handleModalClose }: UserAuthFormProps) {
  return (
    <Box display={{ xs: 'none', lg: 'flex' }}>
      <UserMenu handleModalClose={handleModalClose} />
      <AutoMockUps />
      <AnnounceKitComponent />
    </Box>
  );
}

export function UserMenu({ handleModalClose }: UserAuthFormProps) {
  const profile = useAuth();
  const matches = useMatches();
  const location = useLocation();
  const { t } = useTranslation('root');
  const { flags } = useRouteLoaderData('root') as RootLoaderData;

  const [userMenuAnchorEl, setUserMenuAnchorEl] = React.useState<null | HTMLElement>(null);

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    handleModalClose?.();
    setUserMenuAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchorEl(null);
  };

  const menuOptions = [
    ...dashboardLinks.dashboard
      .filter((link) => !link.flag || flags[link.flag])
      .map((link) => ({
        name: link.text,
        component: <MenuEntry entry={link.text} href={link.to} icon={link.icon} />,
      })),
    {
      name: 'Divider',
      component: React.Fragment,
    },
    ...dashboardLinks.profile.map((link) => ({
      name: link.text,
      component: (id?: number) => <MenuEntry entry={link.text} href={link.to} icon={link.icon} />,
    })),
    dashboardLinks.logout(true),
  ];

  const user = matches[1]?.data?.user;
  const account = user?.requested_account ?? user?.current_account;

  return profile ? (
    <>
      <IconButton
        color="inherit"
        aria-label="user menu"
        aria-haspopup="true"
        onClick={handleUserMenuOpen}
        sx={{ p: 0.75 }}
      >
        <PersonOutlinedIcon sx={{ color: 'primary.dark' }} />
      </IconButton>
      <Menu
        anchorEl={userMenuAnchorEl}
        id="user-menu"
        keepMounted
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        open={Boolean(userMenuAnchorEl)}
        onClose={handleUserMenuClose}
        PaperProps={{ sx: { width: 273, '.MuiIconButton-root': { pl: 0 } } }}
      >
        {user && (
          <UserAvatar
            first_name={user.first_name}
            last_name={user.last_name}
            profile_picture={user.profile_picture}
            current_account={account}
          />
        )}
        <Divider sx={{ mx: 2, my: 1 }} />
        {menuOptions.map((option) => {
          if (option.name === 'Divider') {
            return <Divider key={option.name} sx={{ my: 0.5, mx: 2, px: 2 }} />;
          }
          if (typeof option.component === 'function') {
            return (
              <MenuItem key={option.name} onClick={handleUserMenuClose} sx={{ p: 0, py: 0.5 }}>
                {option.component(account?.id)}
              </MenuItem>
            );
          } else {
            return (
              <MenuItem key={option.name} onClick={handleUserMenuClose} sx={{ p: 0, py: 0.5 }}>
                {option.component}
              </MenuItem>
            );
          }
        })}
      </Menu>
    </>
  ) : (
    <Stack direction="row" flexWrap="nowrap" alignItems="center" gap={2} mx={1.5}>
      <Form method="post" action={`/login?redirectTo=${location.pathname}${location.search}`}>
        <Button
          type="submit"
          variant="outlined"
          aria-label="sign in"
          name="action"
          value="login"
          sx={{
            whiteSpace: 'nowrap',
            color: 'primary.dark',
            bgcolor: 'transparent',
            borderColor: 'primary.dark',
            textTransform: 'uppercase',
            borderRadius: 2,
            letterSpacing: '0.4px',
            '&::active': { borderColor: 'primary.dark' },
            '&:hover': { borderColor: 'primary.dark' },
          }}
        >
          {t('signIn')}
        </Button>
      </Form>
      <Form method="get" action="/login">
        <Button
          type="submit"
          variant="contained"
          name="screen_hint"
          value="signUp"
          aria-label="sign up"
          sx={{
            whiteSpace: 'nowrap',
            color: '#FFF',
            bgcolor: 'primary.main',
            textTransform: 'uppercase',
            boxShadow: 'none',
            borderRadius: 2,
            letterSpacing: '0.4px',
            '&:hover': { boxShadow: 'none' },
          }}
        >
          {t('signUp')}
        </Button>
      </Form>
    </Stack>
  );
}

function AutoMockUps() {
  const fetcher = useFetcher();
  const artworksFetcher = useFetcher();
  const submit = fetcher.submit;
  const [automockupAnchorEl, setAutomockupAnchorEl] = React.useState<null | HTMLElement>(null);
  let [searchParams] = useSearchParams();
  const automockupLogoUrl = searchParams.get(LOGO_PARAM);
  const { detectedCompany, userFirstLanding, keepModalOpen } = useRouteLoaderData('root') as {
    detectedCompany: { name: string; domain: string };
    userFirstLanding: boolean;
    keepModalOpen: boolean;
  };
  const handleAutomockupOpen = (element: HTMLElement | null) => {
    setAutomockupAnchorEl(element);
    if (!element && keepModalOpen) {
      submit(
        { _action: 'keep-modal-open', value: 'false' },
        { action: '/api/artworks', method: 'post', replace: true }
      );
    }
  };
  const initialLogos = React.useMemo<string[]>(
    () => artworksFetcher?.data?.artworks?.results?.map((artwork: Artwork) => artwork.image) ?? [],
    [artworksFetcher.data?.artworks]
  );
  React.useEffect(() => {
    if (artworksFetcher.type === 'init' && Boolean(automockupAnchorEl)) {
      artworksFetcher.load('/api/artworks');
    }
  }, [artworksFetcher, automockupAnchorEl]);

  const isLoading = !artworksFetcher?.data?.artworks;
  const showAutoMockups = useFlags()['show-auto-mockups-temp-05042023'];

  React.useEffect(() => {
    const automockupEl = document.querySelector<HTMLElement>('#automockup-popover-button');

    if (keepModalOpen) {
      setAutomockupAnchorEl(automockupEl);
    }

    if (userFirstLanding) {
      if (detectedCompany && !automockupLogoUrl && automockupEl) {
        setAutomockupAnchorEl(automockupEl);
      }
      submit(
        { _action: 'set-user-first-landing', value: 'false' },
        { action: '/api/artworks', method: 'post', replace: true }
      );
    }
  }, [automockupLogoUrl, detectedCompany, submit, userFirstLanding, keepModalOpen]);

  if (!showAutoMockups) return null;

  return (
    <>
      <IconButton
        aria-label="automockup button"
        id="automockup-popover-button"
        onClick={({ currentTarget }) => handleAutomockupOpen(currentTarget)}
      >
        <StarSetAnimatedIcon />
      </IconButton>
      <Popover
        id="automockup-popover"
        open={Boolean(automockupAnchorEl)}
        anchorEl={automockupAnchorEl}
        onClose={() => handleAutomockupOpen(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        sx={{ zIndex: 1300, maxWidth: 460 }}
      >
        <AutoMockupContainer
          initialLogos={initialLogos}
          isLoadingData={isLoading}
          detectedCompany={detectedCompany}
          onCloseAutomockup={() => handleAutomockupOpen(null)}
        />
      </Popover>
    </>
  );
}

type UserAuthFormMobileProps = {
  setOpenModal: React.Dispatch<React.SetStateAction<boolean>>;
  showMarginTop?: boolean;
};
function UserAuthFormMobile({ setOpenModal, showMarginTop }: UserAuthFormMobileProps) {
  const { t } = useTranslation('root');
  const profile = useAuth();
  const location = useLocation();

  return (
    <>
      {profile ? (
        <Stack mt={showMarginTop ? 3 : 0}>
          <Divider sx={{ borderColor: 'primary.main' }} />
          <Stack pt={1}>
            <Form method="post" action="/logout">
              <Button
                type="submit"
                variant="text"
                aria-label="sign out"
                fullWidth={true}
                onClick={() => setOpenModal(false)}
                sx={{ px: 0, color: 'text.primary', justifyContent: 'start' }}
              >
                <Typography variant="subtitle1">{t('logOut')}</Typography>
              </Button>
            </Form>
          </Stack>
        </Stack>
      ) : (
        <Stack mt={showMarginTop ? 3 : 0}>
          <Divider sx={{ borderColor: 'primary.main' }} />
          <Stack mt={3} mb={1.5}>
            <Form method="post" action={`/login?redirectTo=${location.pathname}${location.search}`}>
              <Button
                type="submit"
                variant="outlined"
                aria-label="sign in"
                fullWidth={true}
                onClick={() => setOpenModal(false)}
                sx={{
                  mb: 2,
                  color: 'primary.dark',
                  borderColor: 'primary.dark',
                  borderRadius: 2,
                  textTransform: 'uppercase',
                }}
              >
                {t('signIn')}
              </Button>
            </Form>
            <Form method="post" action="/login">
              <Button
                type="submit"
                variant="contained"
                aria-label="sign up"
                fullWidth={true}
                onClick={() => setOpenModal(false)}
                sx={{ bgcolor: 'primary.main', borderRadius: 2, textTransform: 'uppercase' }}
              >
                {t('signUp')}
              </Button>
            </Form>
          </Stack>
        </Stack>
      )}
    </>
  );
}

export function MoreOptions() {
  const { flags } = useRouteLoaderData('root') as RootLoaderData;
  const [openModal, setOpenModal] = React.useState(false);
  const [modalId, setModalId] = React.useState(0);
  const [animationLeft, setAnimationLeft] = React.useState(false);
  const [animationRight, setAnimationRight] = React.useState(false);
  const matches = useMatches();
  const { designLabQueueQty }: { designLabQueueQty: number } = matches[0].data;

  const handleModalOpen = () => {
    setOpenModal(true);
  };

  const handleModalClose = () => {
    setOpenModal(false);
    setAnimationLeft(false);
    setAnimationRight(false);
  };

  const user = matches[1]?.data?.user;
  const account = user?.requested_account ?? user?.current_account;

  const getMenuMobile = (item: any) => {
    let menu = null;

    if (item.modalId === modalId) {
      menu = (
        <>
          {item.modalId === ModalMenuId.Dashboard && user && (
            <>
              <UserAvatar
                first_name={user.first_name}
                last_name={user.last_name}
                profile_picture={user.profile_picture}
                current_account={account}
              />
              <Divider sx={{ mt: 1, mx: 1 }} />
            </>
          )}
          {item.columns.map((column: any) => (
            <Stack key={column.id}>
              <Typography variant="subtitle2" mb={column.title !== '' ? 2 : 0}>
                {column.title}
              </Typography>
              {column.links
                .filter((link: any) => !link.flag || flags[link.flag])
                .map((link: any) => (
                  <ModalMenuItem
                    key={link.id}
                    icon={link.icon}
                    title={link.name}
                    subtitle={link.description}
                    to={link.to}
                    externalLink={link.externalLink}
                    handleModalClose={handleModalClose}
                  />
                ))}
            </Stack>
          ))}
        </>
      );
    }

    return menu;
  };

  const handleMenuClick = (id: number) => {
    setModalId(id);
    setAnimationLeft(true);
    setAnimationRight(false);
  };

  const handleMenuBackClick = () => {
    setAnimationRight(true);
    setAnimationLeft(false);

    setTimeout(() => {
      setModalId(0);
    }, 500);
  };

  const profile = useAuth();
  const menuItemListAuthFiltered = profile ? menuItemList : menuItemList.filter((item) => !item.userLogIn);

  return (
    <Box display={{ xs: 'flex', lg: 'none' }}>
      <IconButton size="large" color="inherit" onClick={handleModalOpen} aria-label="show more">
        <MenuIcon sx={{ color: 'primary.dark' }} />
      </IconButton>
      <Dialog
        fullScreen
        open={openModal}
        onClose={handleModalClose}
        TransitionComponent={Transition}
        sx={{
          bottom: 'auto',
          maxHeight: '100vh',
          overflowY: 'auto',
        }}
      >
        <Stack sx={{ p: 3, pb: 1.5, overflowX: 'hidden' }}>
          <Stack direction="row" justifyContent="space-between" mb={3}>
            <Logo variant="dark" isResponsive={false} handleModalClose={handleModalClose} />
            <IconButton onClick={handleModalClose}>
              <CloseIcon />
            </IconButton>
          </Stack>
          <Stack
            direction="row"
            width="calc(200vw - 48px)"
            gap={3}
            position="relative"
            className={animationLeft ? 'moveLeft' : animationRight ? 'moveRight' : ''}
          >
            <Stack width="calc(50% - 24px)" p={0}>
              <List>
                {menuItemListAuthFiltered.map((item) => {
                  if (item.type === 'divider') {
                    return <Divider key={item.modalId} sx={{ borderColor: 'primary.main' }} />;
                  }
                  if (item.modalId === ModalMenuId.DesignLab) {
                    return (
                      <Badge
                        key={item.modalId}
                        badgeContent={designLabQueueQty}
                        sx={{
                          '& .MuiBadge-badge': {
                            bgcolor: '#FF9800',
                            top: 16,
                            right: -3,
                          },
                        }}
                      >
                        <MuiRemixLink
                          key={item.modalId}
                          href={item.href}
                          underline="none"
                          variant="subtitle1"
                          color="text.primary"
                          p="12px 0"
                          display="block"
                        >
                          {item.label}
                        </MuiRemixLink>
                      </Badge>
                    );
                  }
                  if (item.type === 'link') {
                    return (
                      <MuiRemixLink
                        key={item.modalId}
                        href={
                          item.modalId === ModalMenuId.AccountSettings && account?.id
                            ? `${item.href}/${account?.id}`
                            : item.href
                        }
                        underline="none"
                        variant="subtitle1"
                        color="text.primary"
                        p="12px 0"
                        display="block"
                      >
                        {item.label}
                      </MuiRemixLink>
                    );
                  } else {
                    return (
                      <ListItemButton
                        key={item.modalId}
                        onClick={() => handleMenuClick(item.modalId)}
                        sx={{ my: 0.5, px: 0 }}
                      >
                        <ListItemText primary={<Typography variant="subtitle1">{item.label}</Typography>} />
                        <NavigateNextIcon />
                      </ListItemButton>
                    );
                  }
                })}
              </List>
              <UserAuthFormMobile setOpenModal={setOpenModal} />
            </Stack>

            <Stack width="calc(50% - 24px)" p={0}>
              <Button
                variant="text"
                startIcon={<NavigateBeforeIcon />}
                onClick={handleMenuBackClick}
                sx={{ color: 'text.secondary', justifyContent: 'start', px: 0 }}
              >
                <Typography color="text.secondary" variant="subtitle1">
                  Menu
                </Typography>
              </Button>
              {menuItemListAuthFiltered.map((item) => getMenuMobile(item))}
            </Stack>
          </Stack>
        </Stack>
      </Dialog>
    </Box>
  );
}

type UserAvatarProps = Pick<UserResponse, 'first_name' | 'last_name' | 'profile_picture' | 'current_account'>;

export function UserAvatar({ first_name, last_name, profile_picture, current_account }: UserAvatarProps) {
  return (
    <Stack direction="row" px={{ xs: 0, md: 2 }} pb={1} pt={0}>
      {profile_picture ? (
        <Stack width={40} height={40} alignItems="center" mr={1} mt={1}>
          <Image
            src={profile_picture}
            width={40}
            height={40}
            style={{ borderRadius: '50%' }}
            alt="User Image"
            responsive={[{ size: { height: 40, width: 40 } }]}
            options={{ fit: 'cover' }}
            dprVariants={[1, 3]}
            loading="lazy"
          />
        </Stack>
      ) : (
        <IconButton disableFocusRipple disableRipple disabled sx={{ height: 48 }}>
          <AccountCircleIcon color="disabled" sx={{ width: 40, height: 40 }} />
        </IconButton>
      )}

      <Stack justifyContent="center">
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 700,
            fontSize: '16px',
            maxWidth: '193px',
            ...ellipsisStyle,
          }}
        >
          {`${first_name} ${last_name}`}
        </Typography>
        {current_account && (
          <Tooltip arrow title={current_account.name} placement="bottom">
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                fontSize: '14px',
                fontWeight: 500,
                maxWidth: '193px',
                ...ellipsisStyle,
              }}
            >
              Account: {current_account.name}
            </Typography>
          </Tooltip>
        )}
        {current_account?.is_test && (
          <Stack direction="row" mt={1}>
            <Status text="Test Account" color={statusColors.green.main} secondaryColor={statusColors.green.secondary} />
          </Stack>
        )}
      </Stack>
    </Stack>
  );
}

export function ModalMenuItem({
  icon,
  title,
  subtitle,
  to,
  handleModalClose,
  externalLink = false,
}: {
  icon: any;
  title: string;
  subtitle: string;
  to: string;
  handleModalClose: () => void;
  externalLink?: boolean;
}) {
  return (
    <Stack direction="row" flexWrap="nowrap" pb={{ xs: 2, md: 0 }}>
      <Box borderRadius="50%" pt={0.5}>
        {icon}
      </Box>
      <Stack ml={2}>
        <SeeMoreDescription
          title={title}
          description={subtitle}
          buttonPosition="start"
          buttonTextExtended="View less"
          buttonTextFolded="View more"
          maxLines={2}
          wrapper={(children) => (
            <MuiRemixLink
              href={to}
              underline="none"
              sx={{ textUnderline: 'none' }}
              color="inherit"
              aria-label={title}
              width="100%"
              onClick={handleModalClose}
              {...(externalLink && { target: '_blank', rel: 'noopener noreferrer' })}
            >
              {children}
            </MuiRemixLink>
          )}
        />
      </Stack>
    </Stack>
  );
}

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="down" ref={ref} {...props} />;
});

const appBarMinHeight = 56;
const modalTransitionDuration = 500;
const appBarTransitionDuration = 100;

type NavbarProps = {
  transparent?: boolean;
  openCart: boolean;
  setOpenCart: React.Dispatch<React.SetStateAction<boolean>>;
  designLabQueueQty: number;
};
export default function Navbar({ transparent = false, openCart, setOpenCart, designLabQueueQty }: NavbarProps) {
  const [hasScroll] = useHeader();
  const { cart }: { cart: Cart } = useMatches()[0].data;
  const location = useLocation();

  const [openModal, setOpenModal] = React.useState(false);
  const [closingModal, setClosingModal] = React.useState(false);
  const [modalId, setModalId] = React.useState(0);

  const theme = useTheme();
  const inSmallBreakpoint = useMediaQuery(theme.breakpoints.down('sm'));

  const handleModalOpen = (id: number) => {
    setModalId(id);
    if (!openModal) {
      setOpenModal(true);
    } else if (modalId === id) {
      handleModalClose();
    }
  };

  const handleModalClose = () => {
    setOpenModal(false);
    if (openModal) {
      setClosingModal(true);
      setTimeout(setClosingModal, modalTransitionDuration - 145, false);
    }
  };

  const getMenuModal = (item: any) => {
    return item.modalId === modalId
      ? item.columns.map((column: any) => (
          <Stack key={column.id} mt={5}>
            <Typography variant="subtitle2" color="#71718B" mb={2}>
              {column?.title}
            </Typography>
            <Grid
              container
              sx={{
                '& > *:nth-child(n+1)': {
                  pl: 2,
                  pb: 1,
                },
              }}
            >
              {column.links.map((link: any) => {
                return (
                  <Grid item md={4} key={link.id}>
                    <ModalMenuItem
                      key={link.id}
                      icon={link.icon}
                      title={link.name}
                      subtitle={link.description}
                      to={link.to}
                      externalLink={link.externalLink}
                      handleModalClose={handleModalClose}
                    />
                  </Grid>
                );
              })}
            </Grid>
          </Stack>
        ))
      : null;
  };
  return (
    <>
      <AppBar
        id="navbar"
        position="fixed"
        elevation={hasScroll && !openModal ? 4 : 0}
        sx={[
          {
            borderBottom: (!hasScroll && !transparent) || openModal ? 'solid 1px' : 'none',
            borderBottomColor: 'primary.main',
            zIndex: (theme) => theme.zIndex.modal,
            bgcolor: hasScroll || !transparent ? '#FFF' : 'transparent',
            transition: `background-color ${
              openModal ? 0 : appBarTransitionDuration
            }ms ease-in-out,background-position ${openModal ? 0 : appBarTransitionDuration}ms ease-in-out`,
          },
          !hasScroll &&
            transparent && {
              background: 'linear-gradient(to bottom, transparent 50%, #FFF 50%)',
              backgroundSize: '100% 200%',
              backgroundPosition: openModal || closingModal ? '0 -100%' : '0 0',
            },
        ]}
      >
        <Container
          maxWidth={false}
          sx={{ py: 1, maxWidth: location.pathname.includes('embedded-design-lab') ? '100%' : '1440px' }}
        >
          <Toolbar
            variant="dense"
            sx={[
              { minHeight: appBarMinHeight, px: '0 !important' },
              openCart && {
                width: {
                  md: `calc(100vw - ${cartWidth + 50}px)`,
                  lg: `calc(100vw - ${cartWidth + 45}px)`,
                },
                [theme.breakpoints.up(1320)]: { width: `calc(100vw - ${cartWidth + 85}px)` },
                [theme.breakpoints.up(1535)]: { width: 'calc(100% - 50px)' },
                [theme.breakpoints.up(1620)]: { width: '100%' },
              },
            ]}
          >
            <Logo variant="dark" isResponsive={false} handleModalClose={handleModalClose} />
            <Stack
              direction="row"
              width="100%"
              alignItems="center"
              display={{ xs: 'none', lg: 'flex' }}
              ml={{ lg: 0.5, xl: 2 }}
            >
              {menuItemList
                .filter((item) => item.showInDesktopView)
                .map((item) => {
                  if (item.modalId === 5) {
                    return (
                      <MuiRemixLink
                        key={item.modalId}
                        href={item.href}
                        underline="none"
                        variant="subtitle1"
                        color="text.primary"
                        ml={1}
                        flex="0 0 auto"
                      >
                        {item.label}
                      </MuiRemixLink>
                    );
                  }
                  if (item.modalId === 10) {
                    return (
                      <Badge
                        key={item.modalId}
                        badgeContent={designLabQueueQty}
                        sx={{ '& .MuiBadge-badge': { bgcolor: '#FF9800', top: 4, right: 12 } }}
                      >
                        <MuiRemixLink
                          key={item.modalId}
                          href={item.href}
                          underline="none"
                          variant="subtitle1"
                          color="text.primary"
                          ml={2}
                          mr={2}
                        >
                          {item.label}
                        </MuiRemixLink>
                      </Badge>
                    );
                  }

                  return (
                    <Button
                      key={item.modalId}
                      variant="text"
                      disableRipple
                      disableTouchRipple
                      endIcon={
                        <ExpandMoreIcon
                          sx={[
                            { transition: 'transform 350ms ease' },
                            modalId === item.modalId && openModal && { transform: 'rotate(-180deg)' },
                          ]}
                        />
                      }
                      onClick={() => handleModalOpen(item.modalId)}
                      sx={{
                        fontSize: '1rem',
                        color: 'text.primary',
                        background: 'transparent !important',
                        '& > .MuiButton-endIcon': {
                          ml: { lg: 0, xl: 0.5 },
                        },
                      }}
                    >
                      {item.label}
                    </Button>
                  );
                })}
              <Dialog
                keepMounted
                fullScreen
                open={openModal}
                onClose={handleModalClose}
                PaperProps={{ elevation: 0 }}
                TransitionComponent={Slide}
                transitionDuration={modalTransitionDuration}
                sx={{
                  zIndex: ({ zIndex }) => zIndex.modal - 1,
                  bottom: 'auto',
                  '&, & .MuiBackdrop-root': {
                    top: appBarMinHeight + 16,
                  },
                }}
              >
                <Container sx={{ py: 4 }}>
                  <Grid container spacing={6}>
                    {menuItemList.filter((item) => item.showInDesktopView).map((item) => getMenuModal(item))}
                  </Grid>
                </Container>
              </Dialog>
            </Stack>
            <Stack direction="row" alignItems="center" flexWrap="nowrap" width="100%" justifyContent="flex-end">
              <Grid container alignItems="center" justifyContent="flex-end" sx={{ maxWidth: 272, textAlign: 'right' }}>
                <Search
                  isMobile={inSmallBreakpoint}
                  onOpen={handleModalClose}
                  path={
                    location.pathname.includes('shop') &&
                    (location.pathname.includes('bulk-swag') || location.pathname.includes('swag-packs'))
                      ? location.pathname
                      : '/shop'
                  }
                />
              </Grid>
              <UserAuthForm handleModalClose={handleModalClose} />
              {inSmallBreakpoint && <AnnounceKitComponent />}
              <CartButton totalProducts={cart.products.length} setOpenCart={setOpenCart} />
              <MoreOptions />
            </Stack>
          </Toolbar>
        </Container>
      </AppBar>
    </>
  );
}

type SearchProps = {
  alwaysOpen?: boolean;
  isMobile?: boolean;
  path?: string;
  onOpen?: () => void;
  onSearch?: () => void;
  onChange?: (e: any) => void;
};
export function Search({
  alwaysOpen = false,
  isMobile = false,
  path = '/shop',
  onOpen,
  onSearch,
  onChange,
}: SearchProps) {
  const { t } = useTranslation('root');
  const [query] = useSearchParams();
  const [logoParam, automockupParam, domainParam] = [
    query.get(LOGO_PARAM),
    query.get(AUTOMOCKUPS_PARAM),
    query.get(DOMAIN_PARAM),
  ];
  const search = query.get(SEARCH_PARAM) ?? '';

  const [open, setOpen] = React.useState(false);

  const inputRef = React.useRef<HTMLInputElement>();

  const handleClick = () => {
    onOpen?.();
    // Wrapping this in setTimeout to ensure that it's called
    // after closing modal calls by moving execution to the end of the stack
    setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
  };

  const handleSubmit = () => {
    handleClose();
    onSearch?.();
  };

  const handleOpen = () => setOpen(true);

  const handleClose = () => setOpen(false);

  const searchInput = (
    <>
      {logoParam && <input type="hidden" name={LOGO_PARAM} value={logoParam} />}
      {automockupParam && <input type="hidden" name={AUTOMOCKUPS_PARAM} value={automockupParam} />}
      {domainParam && <input type="hidden" name={DOMAIN_PARAM} value={domainParam} />}
      <OutlinedInput
        key={search}
        inputRef={inputRef}
        name={SEARCH_PARAM}
        defaultValue={search}
        placeholder={t('search')}
        onSubmit={handleSubmit}
        onClick={handleClick}
        onChange={onChange}
        startAdornment={
          <InputAdornment position="start">
            <IconButton
              id="search-btn"
              key="submit"
              type="submit"
              aria-label="search"
              sx={{ p: 0.75, position: 'absolute', left: 0 }}
            >
              <SearchIcon sx={{ color: 'primary.dark' }} />
            </IconButton>
          </InputAdornment>
        }
        sx={{
          borderRadius: '100px',
          paddingRight: 0,
          backgroundColor: 'rgba(25, 118, 210, 0.04)',
          width: '100%',
          border: '1px solid #00598B',
          '& .MuiOutlinedInput-notchedOutline': {
            border: 0,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            border: 0,
          },
          '& input::placeholder': {
            color: 'primary.dark',
            opacity: 1,
          },
          '& .MuiInputBase-input': {
            width: '100%',
            p: '6px 0 6px 14px',
            mr: 2,
            borderRadius: '0 !important',
          },
          '& .MuiInputBase-input:-webkit-autofill': {
            WebkitTransitionDelay: '9999s !important',
            transitionDelay: '9999s !important',
          },
          '& .MuiInputBase-input:-webkit-autofill:hover': {
            WebkitTransitionDelay: '9999s !important',
            transitionDelay: '9999s !important',
          },
          '& .MuiInputBase-input:-webkit-autofill:focus': {
            WebkitTransitionDelay: '9999s !important',
            transitionDelay: '9999s !important',
          },
          '& .MuiInputBase-input:-webkit-autofill:active': {
            WebkitTransitionDelay: '9999s !important',
            transitionDelay: '9999s !important',
          },
        }}
        size="small"
      />
    </>
  );

  return (
    <>
      {alwaysOpen ? (
        <Form method="get" action={path} onSubmit={handleSubmit} style={{ width: '100%' }}>
          {searchInput}
        </Form>
      ) : isMobile ? (
        <>
          <IconButton id="search-btn" onClick={handleOpen} aria-label="search" sx={{ mr: 0 }}>
            <SearchIcon sx={{ color: 'primary.dark' }} />
          </IconButton>
          <Dialog
            fullScreen
            open={open}
            onClose={handleClose}
            hideBackdrop={true}
            TransitionComponent={Transition}
            disableEnforceFocus
            disableScrollLock
            PaperProps={{ style: { pointerEvents: 'auto' } }}
            sx={{ height: '72px', pointerEvents: 'none' }}
          >
            <Form method="get" action={path} onSubmit={handleSubmit} style={{ width: '100%' }}>
              <Stack p={2}>
                <Stack direction="row">
                  {searchInput}
                  <IconButton onClick={handleClose} sx={{ ml: 1 }}>
                    <CloseIcon />
                  </IconButton>
                </Stack>
              </Stack>
            </Form>
          </Dialog>
        </>
      ) : (
        <Form method="get" action={path} onSubmit={handleSubmit} style={{ width: '100%' }}>
          {searchInput}
        </Form>
      )}
    </>
  );
}
